import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { firebaseService } from '../services/firebaseService';

interface ThemeColors {
  primary: string;
  secondary: string;
}

interface ThemeContextType {
  colors: ThemeColors;
  loading: boolean;
  darkMode: boolean;
  toggleDarkMode: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { userProfile } = useAuth();
  const [colors, setColors] = useState<ThemeColors>({
    primary: '#3B82F6', // Azul padrão
    secondary: '#10B981' // Verde padrão
  });
  const [loading, setLoading] = useState(true);
  const [darkMode, setDarkMode] = useState<boolean>(() => {
    // Carregar preferência do localStorage
    const saved = localStorage.getItem('darkMode');
    if (saved !== null) {
      return saved === 'true';
    }
    // Verificar preferência do sistema
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    loadThemeColors();
  }, [userProfile?.empresaCnpj]);

  useEffect(() => {
    // Aplicar modo escuro no documento
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    // Salvar preferência
    localStorage.setItem('darkMode', darkMode.toString());
  }, [darkMode]);

  const toggleDarkMode = () => {
    setDarkMode(prev => !prev);
  };

  // Função auxiliar para escurecer uma cor
  const darkenColor = (color: string, amount: number): string => {
    // Remove # se presente
    const hex = color.replace('#', '');
    
    // Converte para RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    // Escurece
    const newR = Math.max(0, Math.floor(r * (1 - amount)));
    const newG = Math.max(0, Math.floor(g * (1 - amount)));
    const newB = Math.max(0, Math.floor(b * (1 - amount)));
    
    // Converte de volta para hex
    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
  };

  const applyThemeColors = (themeColors: ThemeColors) => {
    // Aplicar cores como variáveis CSS no root
    const root = document.documentElement;
    root.style.setProperty('--color-primary', themeColors.primary);
    root.style.setProperty('--color-primary-600', themeColors.primary);
    root.style.setProperty('--color-primary-700', darkenColor(themeColors.primary, 0.1));
    root.style.setProperty('--color-secondary', themeColors.secondary);
    root.style.setProperty('--color-secondary-600', themeColors.secondary);
    
    // Aplicar também em elementos com classes primary-600, primary-700, etc.
    // Isso permite que os componentes existentes usem as cores dinamicamente
  };

  const loadThemeColors = async () => {
    if (!userProfile?.empresaCnpj) {
      setLoading(false);
      return;
    }

    try {
      const empresa = await firebaseService.getEmpresa(userProfile.empresaCnpj);
      
      if (empresa?.coresTema) {
        const themeColors = {
          primary: empresa.coresTema.primary || '#3B82F6',
          secondary: empresa.coresTema.secondary || '#10B981'
        };
        
        setColors(themeColors);
        applyThemeColors(themeColors);
      } else {
        // Usar cores padrão
        applyThemeColors(colors);
      }
    } catch (error) {
      console.error('Erro ao carregar cores do tema:', error);
      applyThemeColors(colors);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeContext.Provider value={{ colors, loading, darkMode, toggleDarkMode }}>
      {children}
    </ThemeContext.Provider>
  );
};

